import isMetric from 'util/numbers/is-metric';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonth(date) {

    return months[date.getMonth()];

}

function formatDay(date, withYear) {

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    let dateString = `${date.getDate()} ${formatMonth(date)}`;

    if (withYear) {
        dateString += ', ' + date.getFullYear().toString().substring(2);
    }

    return dateString;

}

// "X seconds|minutes|hours|days ago"

const multipliers = {
    sec: 1000,
    min: 60000,
    hours: 3600000,
    days: 86400000
};

function convert(date, interval, now = new Date()) {

    return Math.round(Math.abs(now - date) / multipliers[interval]);

}

function readableInterval(dateA, dateB = new Date()) {

    const seconds = convert(dateA, 'sec', dateB);

    if (seconds < 60) {

        return {
            num: seconds,
            unit: 'sec'
        };

    }

    const minutes = convert(dateA, 'min', dateB);

    if (minutes < 60) {

        return {
            num: minutes,
            unit: 'min'
        };

    }

    const hours = convert(dateA, 'hours', dateB);

    if (hours < 24) {

        return {
            num: hours,
            unit: hours === 1 ? 'hour' : 'hours'
        };

    }

    const days = convert(dateA, 'days', dateB);

    return {
        num: days,
        unit: days === 1 ? 'day' : 'days'
    };

}

function formatDate(date) {

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    const readable = readableInterval(date, new Date());

    return `${readable.num} ${readable.unit} ago`;

}

formatDate.forPython = date => date.toISOString().replace(/\..*Z$/, 'Z');

formatDate.getDaysAgo = days => {

    if (days === -1) {

        return;

    }

    const date = new Date();

    if (days === 1) { // filter for "today", not "last 24 hours"

        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);

    } else {

        date.setDate(date.getDate() - days);

    }

    return date;

};

formatDate.getDaysAgoForPython = days => {

    if (days === -1) {
        return undefined;
    }

    return formatDate.forPython(formatDate.getDaysAgo(days));

};

function formatAMPM(date) {

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    let hours = date.getHours();

    const ampm = hours >= 12 ? 'pm' : 'am';

    hours = hours % 12 || 12;

    let minutes = date.getMinutes();

    minutes = minutes < 10 ? '0' + minutes : minutes;

    return `${hours}:${minutes}${ampm}`;
}

/*
Returns one of the following:
Today at 5:00pm (if the date is today)
2 Jun at 5:00pm (if not today)
2 Jun, 2019 at 5:00pm (if date is not this year)
*/
formatDate.dayMonthAtTime = date => {
    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    let dayMonth;
    const time = formatAMPM(date);
    const now = new Date();

    if (now - date < multipliers.days && now.getDate() === date.getDate()) {
        dayMonth = 'Today';
    } else if (now.getFullYear() === date.getFullYear()) {
        dayMonth = `${date.getDate()} ${formatMonth(date)}`;
    } else {
        const year = date.getFullYear().toString().substring(2);
        dayMonth = `${date.getDate()} ${formatMonth(date)}, '${year}`;
    }

    return `${dayMonth} at ${time}`;
};


/*
Returns the following:
2 Jun, 2019 at 5:00pm
*/
formatDate.dayMonthYear = date => {
    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    const year = date.getFullYear();
    const dayMonthYear = `${date.getDate()} ${formatMonth(date)}, ${year}`;

    return `${dayMonthYear}`;
};


formatDate.timestamp = date => {

    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    const now = new Date();

    if (now - date < multipliers.days && now.getDate() === date.getDate()) {

        return formatAMPM(date);

    }

    if (now.getFullYear() === date.getFullYear()) {

        return `${date.getDate()} ${formatMonth(date)}`;

    }

    const year = date.getFullYear().toString().substring(2);

    return `${date.getDate()} ${formatMonth(date)}, '${year}`;

};

formatDate.ddmmmyy = date => {

    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear().toString().substring(2)}`;

};

formatDate.ddmmyyyy = date => {

    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    return `${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear().toString()}`;
};

formatDate.ddmm = date => {

    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    return `${date.getDate()} ${months[date.getMonth()]}`;
};

formatDate.ddmmyy = date => {

    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    const monthDay = isMetric()
        ? `${date.getDate()}/${date.getMonth() + 1}`
        : `${date.getMonth() + 1}/${date.getDate()}`;

    return `${monthDay}/${date.getFullYear().toString().slice(2)}`;
};

formatDate.ddMonthYYY = date => {
    if (!date) {
        return 'N/A';
    }

    if (!(date instanceof Date)) {

        date = new Date(date);

    }

    const month = date.toLocaleString('default', {month: 'long'});

    return `${date.getDate()} ${month}, ${date.getFullYear().toString()}`;
};

formatDate.setTimeToZero = date => {

    date.setHours(0);

    date.setMinutes(0);

    date.setSeconds(0);

    date.setMilliseconds(0);

    return date;

};

formatDate.setTimeToMidnight = date => {

    date.setHours(23);

    date.setMinutes(59);

    date.setSeconds(59);

    date.setMilliseconds(999);

    return date;

};

function dateAndTime(date) {
    const AMPM = formatDate.formatAMPM(date);
    if (date.getFullYear() === new Date().getFullYear()) {
        const DDMM = formatDate.ddmm(date);
        if (DDMM.includes('NaN') || AMPM.includes('NaN')) {
            return 'Invalid Date';
        }
        return `${DDMM} at ${AMPM}`;
    }
    const DDMMYYYY = formatDate.ddmmyyyy(date);
    if (DDMMYYYY.includes('NaN') || AMPM.includes('NaN')) {
        return 'Invalid Date';
    }
    return `${DDMMYYYY} at ${AMPM}`;
}

function forceTwoDigits(n) {
    if (n < 10) {
        return '0' + n;
    }
    return n;
}

// The Custom Reporter service expects dates in 
// %Y-%m-%d %H:%M:%S format and UTC tz
formatDate.forCustomReport = date => {
    return `${
        date.getUTCFullYear()
    }-${forceTwoDigits(date.getUTCMonth() + 1)
    }-${forceTwoDigits(date.getUTCDate())
    } ${forceTwoDigits(date.getUTCHours())
    }:${forceTwoDigits(date.getUTCMinutes())
    }:${forceTwoDigits(date.getUTCSeconds())
    }`;
};

formatDate.formatAMPM = formatAMPM;

formatDate.convert = convert;

formatDate.formatDay = formatDay;

formatDate.months = months;

formatDate.formatMonth = formatMonth;

formatDate.readableInterval = readableInterval;

formatDate.dateAndTime = dateAndTime;

export default formatDate;
